import * as jwtDecode from "jwt-decode";

import {
	hydrateLabStateFromLocalStorage,
	hydrateLabStateFromSessionStorage,
	persistLabToSessionStorage,
	persistLabToLocalStorage,
	persistLab,
	getExampleFromHistory,
	persistLabHttpToSessionStorage,
} from "../utils/lab_persist";
import * as Blockv from "../MainBv.js"

import {labHttpState, LabHttpState, methodAllowsBody} from "./lab_http";

export interface LabState {
	isOpen: boolean;
	isFullscreen: boolean;
	isRefreshingToken: boolean;
	splitSize: string | number;

	example: LabExample | undefined;
	defaultExample: LabExample | undefined;

	exampleHistory: LabExample[];

	authedUsers: { [id: string]: AuthedUser };
	authedUser: AuthedUser;
	authedUserDialog: AuthedUserDialog;
}

// example

/**
 * specifies the data structure of any example displayed in the lab
 */
export type LabExample = LabExampleHttp | LabExampleJs;

interface LabExampleCommon {
	// a uniquely identifiable key for the example
	// note: MUST be unique among the specific type of example only
	key: number;

	// the example type
	type: string;

	// the datetime in ISO8601 format
	timestampIso: string;

	// metadata about the example
	meta: LabExampleMeta;

	// example data
	data: any;
}

/**
 * http example
 */
export interface LabExampleHttp extends LabExampleCommon {
	type: "http";
	data: LabExampleHttpData;
}

/**
 * javascript example
 */
export interface LabExampleJs extends LabExampleCommon {
	type: "js";
	data: LabExampleJsData;
}

/**
 * contains metadata about an example
 */
interface LabExampleMeta {
	// the title of the document the example is a part of
	docTitle: string;

	// the title of the request itself, or empty string
	heading: string;
}

/**
 * lab data for http request examples
 */
export interface LabExampleHttpData {
	request: {
		url: string;
		method: string;
		body: string;
		headers: HttpHeadersDict;
		status: RequestStatus;
		authType: string;
		isBodyAllowed: boolean;
	};
	response?: {
		body: string;
		statusCode: number;
		statusText: string;
		duration: number;
		headers: HttpHeadersDict;
	};
}

enum LabExampleJSDataOutputEntryLogLevel {
	Default = "default",
	Info = "info",
	Error = "error",
	Warning = "warning",
}

export interface LabExampleJSDataOutputEntry {
	logLevel: LabExampleJSDataOutputEntryLogLevel;
	value: any;
}

/**
 * lab data for javascript repl examples
 */
export interface LabExampleJsData {
	code: string;
	output: LabExampleJSDataOutputEntry[];

	// note: js examples DO NOT fail
	// instead, an error is appended to the output
	// so `status' will never be set to "failure"
	status: RequestStatus;
}

// end example

export interface AuthedUserDialog {
	authedUser: AuthedUser;
	isOpen: boolean;
}

export interface AuthedUser {
	firstName: string;
	lastName: string;
	isAdmin: boolean;
	accessToken: string;
	refreshToken: string;
	id: string;
}

const INITIAL_CURRENT_USER_STATE: LabState = {
	isOpen: false,
	splitSize: "50%",
	isFullscreen: false,
	authedUsers: null,
	authedUser: null,
	authedUserDialog: {authedUser: null, isOpen: false},
	isRefreshingToken: false,
	exampleHistory: [],
	example: undefined,
	defaultExample: undefined,
};

function modifyInitialState(state: LabState): LabState {
	// do nothing on server-side render
	if (typeof window !== "object") {
		return state;
	}

	try {
		const hydratedFromSession: LabState = hydrateLabStateFromSessionStorage();
		const hydratedFromLocal: LabState = hydrateLabStateFromLocalStorage();

		const mergedState: LabState = {
			...state, // initial lab state
			...hydratedFromLocal, // contains AuthedUsers, AuthedUser, ExampleHistory
			...hydratedFromSession, // contains LabState
		};

		// For backwards compatibility, if no example is retrieved from storage, close the Lab
		if (mergedState.isOpen && !mergedState.example) {
			mergedState.isOpen = false;
		}

		// note: the order here is important, as some local storage takes precedence here
		// but some fields need to then be merged with both values
		return {
			...mergedState,
			authedUser: hydratedFromSession.authedUser,
			authedUsers: {
				...hydratedFromSession.authedUsers,
				...hydratedFromLocal.authedUsers,
			},
		};
	} catch (e) {
		console.error(e);
		return state;
	}
}

export default (state: LabState = modifyInitialState(INITIAL_CURRENT_USER_STATE),
				action: any): LabState => {
	switch (action.type) {
		case "CURRENT_USER/GET_INFO/SUCCESS": {
			const newUser: AuthedUser = buildAuthedUser({
				...action.data.user,
				refreshToken: action.data.refreshToken,
				accessToken: action.data.accessToken,
			});

			return {
				...state,
				authedUsers: {
					...state.authedUsers,
					[newUser.id]: newUser,
				},
				authedUser: state.authedUser || newUser,
			};
		}

		case "CURRENT_USER/LOGGED_OUT": {
			const authedUser: AuthedUser = state.authedUser;
			const authedUsers: { [id: string]: AuthedUser } = {...state.authedUsers};
			const currentUserId = action.data.id;

			delete authedUsers[currentUserId];

			return {
				...state,
				authedUser: authedUser ? currentUserId === authedUser.id ? null : authedUser : null,
				authedUsers,
			};
		}

		case "LAB/OPEN": {
			const example: LabExample = action.data.example;

			return {
				...state,
				isOpen: true,
				example: example || state.example,
				defaultExample: {...(example || state.example)},
			};
		}

		case "LAB/CLOSE": {
			return {
				...state,
				isOpen: false,
				isFullscreen: false,
			};
		}

		case "LAB/TOGGLE_FULLSCREEN": {
			return {
				...state,
				isFullscreen: !state.isFullscreen,
				splitSize: "50%",
			};
		}

		case "LAB/CHANGE_LAB_SIZE": {
			return {
				...state,
				splitSize: action.data.splitSize,
			};
		}

		case "LAB/HISTORY/DELETE_EXAMPLE": {
			const exampleIndex: number = action.data.exampleIndex;

			const exampleHistory = [...state.exampleHistory];
			exampleHistory.splice(exampleIndex, 1);

			return {
				...state,
				exampleHistory,
			};
		}

		case "LAB/CHANGE_EXAMPLE_DATA": {
			return {
				...state,
				example: {
					...state.example,
					data: action.data.exampleData,
				},
			};
		}

		case "LAB/HISTORY/LOAD_EXAMPLE": {
			const example: LabExample = action.data.example;

			if (example.type === "http") {
				const exampleData: LabExampleHttpData = example.data;
				const headers = exampleData.request.headers;
				const authType: string = exampleData.request.authType;

				if (headers.Authorization && authType !== "none") {
					if (authType === "access_token") {
						headers.Authorization = `Bearer ${state.authedUser.accessToken}`;
					} else if (authType === "refresh_token") {
						headers.Authorization = `Bearer ${state.authedUser.refreshToken}`;
					} else {
						throw new Error(`error: unknown authType ("${authType}")`);
					}
				}
			}

			return {
				...state,
				example,
				defaultExample: {...example},
			};
		}

		case "LAB/RESET_REQUEST": {
			// todo: should we reset the response too?

			return {
				...state,
				example: {...state.defaultExample},
			};
		}

		case "LAB/PERFORM_REFRESH/PENDING": {
			return {
				...state,
				isRefreshingToken: true,
			};
		}

		case "LAB/PERFORM_REFRESH/SUCCESS": {
			if (state.example.type !== "http") {
				throw new Error("error: incorrect example type (must be js)");
			}

			const example: LabExampleHttp = state.example;
			const headers = {...example.data.request.headers};

			if (headers.Authorization) {
				headers.Authorization = `Bearer ${action.data.accessToken}`;
			}

			return {
				...state,
				isRefreshingToken: false,
				authedUser: {
					...state.authedUser,
					accessToken: action.data.accessToken,
				},
				authedUsers: {
					...state.authedUsers,
					[state.authedUser.id]: {
						...state.authedUsers[state.authedUser.id],
						accessToken: action.data.accessToken,
					},
				},
				example: {
					...example,
					data: {
						...example.data,
						request: {
							...example.data.request,
							headers,
						},
					},
				},
			};
		}

		case "LAB/REMOVE_AUTHED_USER": {
			const newState = {...state};
			delete newState.authedUsers[action.data.user.id];
			return newState;
		}

		case "LAB/CHANGE_AUTHED_USER": {
			const example: LabExample = state.example;

			// JS example's only need to update the authedUser, as auth tokens are handle at request run time
			if (example.type === "js") {
				return {
					...state,
					authedUser: action.data.authedUser,
				};
			} else {
				// when switching users on an HTTP example, we need to update the headers with the new user's auth token
				const headers = {...example.data.request.headers};

				if (headers.Authorization) {
					if (example.data.request.authType === "refresh_token") {
						headers.Authorization = `Bearer ${action.data.authedUser.refreshToken}`;
					} else {
						headers.Authorization = `Bearer ${action.data.authedUser.accessToken}`;
					}
				}

				return {
					...state,
					authedUser: action.data.authedUser,
					example: {
						...example,
						data: {
							...example.data,
							request: {
								...example.data.request,
								headers,
							},
						},
					},
				};
			}
		}

		case "LAB/CLOSE_AUTHED_USER_DIALOG": {
			return {
				...state,
				authedUser: action.data.authedUser || state.authedUser,
				authedUserDialog: {
					isOpen: false,
					authedUser: null,
				},
			};
		}

		case "LAB/PERFORM_REQUEST/PENDING": {
			if (state.example.type !== "http") {
				throw new Error("error: incorrect example type (must be js)");
			}

			const example: LabExampleHttp = state.example;

			return {
				...state,
				example: {
					...example,
					data: {
						...example.data,
						request: {
							...example.data.request,
							status: "pending",
						},
					},
				},
			};
		}

		case "LAB/PERFORM_REQUEST/SUCCESS": {
			if (state.example.type !== "http") {
				throw new Error("error: incorrect example type (must be js)");
			}

			const example: LabExampleHttp = state.example;
			const newExample: LabExampleHttp = {
				...example,
				timestampIso: action.data.responseDatetimeIso,
				data: {
					...example.data,
					request: {
						...example.data.request,
						status: "success",
					},
					response: {
						...example.data.response,
						body: action.data.responseBody,
						statusCode: action.data.responseStatusCode,
						statusText: action.data.responseStatusText,
						duration: action.data.responseDuration,
						headers: action.data.responseHeaders,
					},
				},
			};

			return {
				...state,
				example: newExample,
				exampleHistory: [
					...state.exampleHistory.filter(ex => !isSameExample(newExample, ex)),
					newExample,
				],
			};
		}

		case "LAB/PERFORM_REQUEST/FAILURE": {
			if (state.example.type !== "http") {
				throw new Error("error: incorrect example type (must be js)");
			}

			const example: LabExampleHttp = state.example;

			return {
				...state,
				example: {
					...example,
					data: {
						...example.data,
						request: {
							...example.data.request,
							status: "failure",
						},
					},
				},
			};
		}

		case "LAB/STORE_AUTHED_USER": {
			return {
				...state,
				authedUsers: {...state.authedUsers, [action.data.authedUser.id]: action.data.authedUser},
				authedUser: action.data.authedUser,
			};
		}

		case "LAB/REQUEST_SWITCH_USER": {
			return {
				...state,
				authedUsers: {...state.authedUsers, [action.data.authedUser.id]: action.data.authedUser},
				authedUserDialog: {
					isOpen: !state.authedUser || state.authedUser.id !== action.data.authedUser.id,
					authedUser: action.data.authedUser,
				},
			};
		}

		case "LAB/JS/RUN_EXAMPLE/PENDING": {
			const example = state.example;

			if (example.type !== "js") {
				throw new Error("error: incorrect example type (must be js)");
			}

			return {
				...state,
				example: {
					...example,
					data: {
						...example.data,
						output: [],
						status: "pending",
					},
				},
			};
		}

		case "LAB/JS/RUN_EXAMPLE/SUCCESS": {
			const example = state.example;
			const output: any[] = action.data.output;

			if (example.type !== "js") {
				throw new Error("error: incorrect example type (must be js)");
			}

			const newExample: LabExampleJs = {
				...example,
				data: {
					...example.data,
					output,
					status: "success",
				},
			};

			return {
				...state,
				example: newExample,
				exampleHistory: [
					...state.exampleHistory.filter(ex => !isSameExample(newExample, ex)),
					newExample,
				],
			};
		}
	}

	return state;
};

export const actions = {
	open: (example: LabExample) => async(dispatch, getState) => {
		const state = labState(getState());
		let myExample: LabExample = example;

		const storedExample = getExampleFromHistory(example.key);

		if (storedExample !== undefined) {
			myExample = storedExample;
		}

		if (myExample.type === "http") {
			if (myExample.data.request.authType === "access_token") {
				myExample.data.request.headers.Authorization = `Bearer ${(state.authedUser ? state.authedUser.accessToken : Blockv.instance.UserManager.getAccessToken())}`;
			} else if (myExample.data.request.authType === "refresh_token") {
				myExample.data.request.headers.Authorization = `Bearer ${state.authedUser ? state.authedUser.refreshToken : Blockv.instance.UserManager.getRefreshToken()}`;
			}
		}

		await dispatch({
			type: "LAB/OPEN",
			data: {
				example: {
					...myExample,
					meta: {
						...myExample.meta,
						heading: myExample.meta.heading.replace("Sample ", ""),
					},
				},
			},
		});

		persistLabToSessionStorage(labState(getState()));
		persistLabHttpToSessionStorage(labHttpState(getState()));
	},
	close: () => async(dispatch, getState) => {
		const state = labState(getState());

		await dispatch({
			type: "LAB/CLOSE",
			data: {
				isFullscreen: state.isFullscreen,
			},
		});

		persistLabToSessionStorage(labState(getState()));
	},
	changeSplitSize: (splitSize: string | number) => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/CHANGE_LAB_SIZE",
			data: {splitSize},
		});

		persistLabToSessionStorage(labState(getState()));
	},
	toggleFullscreen: () => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/TOGGLE_FULLSCREEN",
		});

		persistLabToSessionStorage(labState(getState()));
	},
	loadExampleFromHistory: (example: LabExample) => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/HISTORY/LOAD_EXAMPLE",
			data: {
				example,
			},
		});

		persistLabToSessionStorage(labState(getState()));
	},
	changeExampleData: (exampleData: any) => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/CHANGE_EXAMPLE_DATA",
			data: {exampleData},
		});

		const state = labState(getState());
		persistLabToSessionStorage(state);
	},
	resetLabRequest: () => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/RESET_REQUEST",
		});

		const state = labState(getState());
		persistLabToSessionStorage(state);
	},
	changeAuthedUser: (authedUser: AuthedUser) => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/CHANGE_AUTHED_USER",
			data: {authedUser},
		});

		const state: LabState = labState(getState());
		persistLab(state);
	},
	closeAuthedUserDialog: (authedUser: AuthedUser) => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/CLOSE_AUTHED_USER_DIALOG",
			data: {authedUser},
		});

		const state = labState(getState());
		persistLabToSessionStorage(state);
	},
	removeAuthedUser: (user: AuthedUser) => async(dispatch, getState) => {
		const authedUser: AuthedUser = selectors.selectAuthedUser(getState());
		let authedUsers: { [id: string]: AuthedUser };

		await dispatch({
			type: "LAB/REMOVE_AUTHED_USER",
			data: {user},
		});

		authedUsers = selectors.selectAuthedUsers(getState());

		// When removing the currently selected Lab user, reset the user to the first in the list
		if (user.id === authedUser.id) {
			await dispatch(actions.changeAuthedUser(authedUsers[Object.keys(authedUsers)[0]]));
		}

		const state: LabState = labState(getState());
		persistLab(state);
	},
	deleteExample: (exampleIndex: number) => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/HISTORY/DELETE_EXAMPLE",
			data: {
				exampleIndex,
			},
		});

		const state: LabState = labState(getState());
		persistLab(state);
	},
	handleRefreshTokenSuccess: resBody => (dispatch, getState) => {
		let state = labState(getState());
		const authedUser = {...state.authedUser};

		authedUser.accessToken = resBody.payload.access_token.token;

		dispatch({
			type: "LAB/STORE_AUTHED_USER",
			data: {
				authedUser,
			},
		});

		state = labState(getState());
		persistLab(state);
	},
	handleLabLoginSuccess: userData => async(dispatch, getState) => {
		console.log("login success");
		const authedUser = buildAuthedUser(userData);

		await dispatch({
			type: "LAB/REQUEST_SWITCH_USER",
			data: {
				authedUser,
			},
		});

		const state: LabState = labState(getState());
		persistLab(state);
	},
	handleLabReauthorization: () => async(dispatch, getState) => {
		let state: LabState = labState(getState());
		const {authedUser, example} = state;

		await dispatch({
			type: "LAB/PERFORM_REFRESH/PENDING",
		});

		// todo: work better for non-http example

		const res = await fetch("https://api.blockv.io/v1/access_token", {
			method: "POST",
			headers: {
				"App-Id": (example.data as LabExampleHttpData).request.headers["App-Id"],
				Authorization: `Bearer ${authedUser.refreshToken}`,
			},
			mode: "cors",
		});

		const resBody = await res.json();

		const accessToken = resBody.payload.access_token.token;

		await dispatch({
			type: "LAB/PERFORM_REFRESH/SUCCESS",
			data: {accessToken},
		});

		state = labState(getState());
		persistLab(state);
	},
	// todo: make this work better for other example types
	performRequest: () => async(dispatch, getState) => {
		await dispatch({
			type: "LAB/PERFORM_REQUEST/PENDING",
		});

		let state: LabState = labState(getState());
		const example: LabExampleHttp = labState(getState()).example as LabExampleHttp;

		const url: string = example.data.request.url;
		const method: string = example.data.request.method;
		const body: string = methodAllowsBody(method, example.data.request.body)
			? example.data.request.body
			: undefined;
		const headers: HttpHeadersDict = example.data.request.headers;
		const startDate: Date = new Date();
		const isLoginAttempt: boolean = !!url.match(/user\/login$/);
		const isRefreshAttempt: boolean = !!url.match(/access_token$/);
		let res;
		let resBody;

		if (headers["App-Id"] && headers["App-Id"].indexOf("{app_id}") > -1) {
			headers["App-Id"] = process.env.BLOCKV_APP_ID;
		}

		try {
			res = await fetch(url, {
				method,
				body,
				headers,
				mode: "cors",
			});

			resBody = await res.json();
		} catch (e) {
			await dispatch({type: "LAB/PERFORM_REQUEST/FAILURE"});
			persistLabToSessionStorage(labState(getState()));
			return;
		}

		const endDate: Date = new Date();
		const responseDuration = endDate.getTime() - startDate.getTime();
		const responseHeaders: HttpHeadersDict = {};

		res.headers.forEach((headerValue, headerName) => {
			responseHeaders[headerName] = headerValue;
		});

		// When the request performed in a refresh token attempt, update the refresh token in state
		if (isRefreshAttempt && res.status === 200) {
			dispatch(actions.handleRefreshTokenSuccess(resBody));
		}

		// When the request performed is a login attempt, store the result in the authedUsers mapping
		if (isLoginAttempt && res.status === 200) {
			dispatch(
				actions.handleLabLoginSuccess({
					...resBody.payload.user,
					accessToken: resBody.payload.access_token.token,
					refreshToken: resBody.payload.refresh_token.token,
				})
			);

			// also, clear out any potential passwords stored in the request
			const requestBody = JSON.parse(example.data.request.body);

			// Check for a password and reset it to "secret"
			if (requestBody.auth_data && requestBody.auth_data.password) {
				requestBody.auth_data.password = "secret";
				example.data.request.body = JSON.stringify(requestBody, null, 4);

				// todo: why do we change the lab content? ANS: to clear out the user's password, if one was set
				dispatch(
					actions.changeExampleData({
						...example.data,
						request: {
							...example.data.request,
							body: JSON.stringify(requestBody, null, 4),
						},
					})
				);
			}
		}

		// When we receive a 401 outside of login, we need to refresh the current user's access_token and try the action again
		if (res.status === 401 && !isLoginAttempt) {
			await dispatch(actions.handleLabReauthorization());
			return dispatch(actions.performRequest());
		}

		example.timestampIso = new Date().toISOString();
		example.data.request.status = "success";
		example.data.response = {
			body: JSON.stringify(resBody, null, 2),
			statusCode: res.status,
			statusText: res.statusText,
			duration: responseDuration,
			headers: responseHeaders,
		};

		await dispatch({
			type: "LAB/PERFORM_REQUEST/SUCCESS",
			data: {
				responseBody: JSON.stringify(resBody, null, 2),
				responseStatusCode: res.status,
				responseStatusText: res.statusText,
				responseDuration,
				responseHeaders,
				responseDatetimeIso: new Date().toISOString(),
			},
		});

		state = labState(getState());
		persistLab(state);
	},
	runJsExample: (appId: string) => async(dispatch, getState) => {
		let state: LabState = labState(getState());
		const example = state.example;
		const authedUser = state.authedUser;

		if (example.type !== "js") {
			throw new Error("you cannot run a non-js example");
		}

		let logEntries = [];
		const originalConsole = window.console;

		await dispatch({type: "LAB/JS/RUN_EXAMPLE/PENDING"});

		const Blockv = require("../../vendor/blockv-sdk.min.js");
		window.labBv = new Blockv({
			appID: appId,
			server: process.env.BLOCKV_API_URL,
			websocketAddress: process.env.BLOCKV_API_WS_URL,
			prefix: `lab-${appId}`,
		});

		window.labBv.store.accessToken = authedUser.accessToken;
		window.labBv.store.refreshToken = authedUser.refreshToken;

		/**
		 * create a lab logger with the specified log level
		 * @param logLevel the logger's log level
		 */
		function makeLogger(logLevel: LabExampleJSDataOutputEntryLogLevel) {
			return (...args) => {
				logEntries = [
					...logEntries,
					...args.map(value => ({logLevel, value})),
					{logLevel, value: "\n"},
				];
			};
		}

		// override console methods
		(window.console as any) = {
			log: makeLogger(LabExampleJSDataOutputEntryLogLevel.Default),
			info: makeLogger(LabExampleJSDataOutputEntryLogLevel.Info),
			warn: makeLogger(LabExampleJSDataOutputEntryLogLevel.Warning),
			error: makeLogger(LabExampleJSDataOutputEntryLogLevel.Error),
		};

		window.onLabError = (e: Error) => {
			let message: string = "";

			if (e instanceof Error) {
				message += e.toString();
			} else {
				message = "Error: " + e;
			}

			logEntries.push({
				logLevel: LabExampleJSDataOutputEntryLogLevel.Error,
				value: message,
			});
		};

		// tslint:disable-next-line:no-eval
		const result = eval(`
			const bv = window.labBv;
			async () => {
				try {
					${example.data.code};
				} catch (e) {
					window.onLabError(e);
				}
			};
		`);

		const finished = async resultValue => {
			if (resultValue !== undefined) {
				logEntries.push({
					logLevel: LabExampleJSDataOutputEntryLogLevel.Default,
					value: resultValue,
				});
			}

			// Decode the current access token in the SDK to determine if a new user has logged in
			const labBvUserId = jwtDecode(window.labBv.store.accessToken).user_id;

			// If the SDK's userId is different than the Lab's authedUser's, show the change user dialog
			if (labBvUserId !== authedUser.id) {
				const userData = await window.labBv.UserManager.getCurrentUser();
				userData.accessToken = window.labBv.store.accessToken;
				userData.refreshToken = window.labBv.store.refreshToken;

				await dispatch(actions.handleLabLoginSuccess(userData));
			}

			await dispatch({
				type: "LAB/JS/RUN_EXAMPLE/SUCCESS",
				data: {
					output: logEntries,
				},
			});

			state = labState(getState());
			persistLab(state);
		};

		const cleanupAfterRunningExample = () => {
			window.console = originalConsole;

			// remove the last newline
			// logEntries.splice(logEntries.length - 1, 1);
		};

		await Promise.resolve(result())
			.then(resultValue => {
				cleanupAfterRunningExample();
				return finished(resultValue);
			})
			.catch(e => {
				cleanupAfterRunningExample();
				return finished(undefined);
			});
	},
};

// helper to get this reducer's state as a typed value
const labState = state => state.lab as LabState;

export const selectors = {
	selectIsOpen: state => labState(state).isOpen,
	selectIsFullscreen: state => labState(state).isFullscreen,
	selectSplitSize: state => labState(state).splitSize,
	selectExample: state => labState(state).example,

	selectTitle: state => labState(state).example.meta.docTitle,
	selectHeading: state => labState(state).example.meta.heading,

	selectAuthedUsers: state => labState(state).authedUsers,
	selectAuthedUser: state => labState(state).authedUser,
	selectAuthedUserDialog: state => labState(state).authedUserDialog,
	selectIsRefreshingToken: state => labState(state).isRefreshingToken,

	selectExampleHistory: (state, type: string) =>
		labState(state).exampleHistory.filter(ex => ex.type === type),
};

/**
 * returns true if the two provided examples are the same
 * @param ex1 the first example
 * @param ex2 the second example
 */
export const isSameExample = (ex1: LabExample, ex2: LabExample): boolean =>
ex1.type === ex2.type && ex1.key === ex2.key;

// helper to build authedUser object from payload
const buildAuthedUser = (userData: any): AuthedUser => ({
	firstName: userData.first_name,
	lastName: userData.last_name,
	id: userData.id,
	isAdmin: userData.is_admin,
	accessToken: userData.accessToken,
	refreshToken: userData.refreshToken,
});



// WEBPACK FOOTER //
// ./src/reducers/lab.ts